import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { paths } from '@/routes/paths'
import { X, Music } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { uploadFile } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fError } from '@/utils/format-error'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import { zodResolver } from '@hookform/resolvers/zod'
import axiosInstance, { endpoints } from '@/lib/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import type {
  TrendSound,
  TrendSoundType,
  CreateTrendSoundData,
  UpdateTrendSoundData,
  FileUpload,
} from './types'

import { TREND_SOUND_TYPE_OPTIONS } from './types'

// Schema definition
const trendSoundFormSchema = z.object({
  title: z.string().min(1, 'กรุณาป้อนชื่อเสียง'),
  artist: z.string().optional(),
  type: z.enum(['original', 'music', 'sound_effect', 'voice_over']),
  duration: z.number().min(0).optional(),
})

type FormData = z.infer<typeof trendSoundFormSchema>

interface TrendSoundFormProps {
  trendSound?: TrendSound
  mode: 'create' | 'edit'
}

export default function TrendSoundForm({
  trendSound,
  mode,
}: TrendSoundFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)
  const [coverImage, setCoverImage] = useState<FileUpload | null>(
    trendSound?.coverUrl
      ? {
          url: trendSound.coverUrl,
          fileName: 'cover-image',
        }
      : null,
  )
  const [audioFile, setAudioFile] = useState<FileUpload | null>(
    trendSound?.audioUrl
      ? {
          url: trendSound.audioUrl,
          fileName: 'audio-file',
        }
      : null,
  )
  const [tags, setTags] = useState<string[]>(trendSound?.tags || [])
  const [tagInput, setTagInput] = useState('')

  const form = useForm<FormData>({
    resolver: zodResolver(trendSoundFormSchema),
    defaultValues: {
      title: trendSound?.title || '',
      artist: trendSound?.artist || '',
      type: (trendSound?.type as TrendSoundType) || 'original',
      duration: trendSound?.duration || 0,
    },
  })

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCoverImage({
        url: file,
        fileName: file.name,
      })
    }
  }

  const handleAudioFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAudioFile({
        url: file,
        fileName: file.name,
      })
      // Extract duration from audio file
      const audio = new Audio()
      audio.src = URL.createObjectURL(file)
      audio.onloadedmetadata = () => {
        form.setValue('duration', Math.round(audio.duration))
        URL.revokeObjectURL(audio.src)
      }
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove))
  }

  const onSubmit = async (data: FormData) => {
    try {
      submitting.onTrue()

      // Upload cover image
      let coverImageUrl = trendSound?.coverUrl || ''
      if (coverImage && typeof coverImage.url !== 'string') {
        coverImageUrl = await uploadFile(
          coverImage.url,
          'trend-sounds/covers',
          coverImage.fileName,
        )
      }

      // Upload audio file
      let audioUrl = trendSound?.audioUrl || ''
      if (audioFile && typeof audioFile.url !== 'string') {
        audioUrl = await uploadFile(
          audioFile.url,
          'trend-sounds/audio',
          audioFile.fileName,
        )
      }

      if (!audioUrl) {
        toast.error('กรุณาอัปโหลดไฟล์เสียง')
        return
      }

      // Prepare trend sound data
      const trendSoundData = {
        title: data.title,
        artist: data.artist || undefined,
        audioUrl,
        coverUrl: coverImageUrl || undefined,
        duration: data.duration || 0,
        type: data.type,
        tags: tags.length > 0 ? tags : undefined,
      }

      if (mode === 'edit' && trendSound) {
        // Update existing trend sound
        const updateData: UpdateTrendSoundData = trendSoundData
        await axiosInstance.put(
          endpoints.trendSounds.update(trendSound.id),
          updateData,
        )
        toast.success('แก้ไขเสียงเทรนด์สำเร็จ')
      } else {
        // Create new trend sound
        const createData: CreateTrendSoundData = trendSoundData
        await axiosInstance.post(endpoints.trendSounds.create, createData)
        toast.success('สร้างเสียงเทรนด์สำเร็จ')
      }

      router.push(paths.trendSounds)
    } catch (error) {
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  const handleBack = () => {
    router.push(paths.trendSounds)
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          id="trend-sound-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    ข้อมูลหลัก
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ชื่อเสียง <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="ป้อนชื่อเสียง"
                            className="rounded-lg border"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Artist */}
                  <FormField
                    control={form.control}
                    name="artist"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ศิลปิน
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="ป้อนชื่อศิลปิน"
                            className="rounded-lg border"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Type */}
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ประเภท
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="rounded-lg border">
                              <SelectValue placeholder="เลือกประเภท" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TREND_SOUND_TYPE_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">
                      แท็ก
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="ป้อนแท็ก"
                        className="rounded-lg border"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleAddTag()
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddTag}
                      >
                        เพิ่ม
                      </Button>
                    </div>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {tags.map((tag) => (
                          <div
                            key={tag}
                            className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-lg text-sm"
                          >
                            <span>{tag}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Files Section */}
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    ไฟล์
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Audio File */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">
                      ไฟล์เสียง <span className="text-destructive">*</span>
                    </Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="audio/*"
                        onChange={handleAudioFileChange}
                        className="rounded-lg border"
                      />
                      {audioFile && (
                        <div className="flex items-center gap-2 p-2 bg-secondary rounded-lg">
                          <Music className="h-4 w-4" />
                          <span className="text-sm">
                            {typeof audioFile.url === 'string'
                              ? audioFile.fileName
                              : audioFile.fileName}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={() => setAudioFile(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cover Image */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">
                      รูปปก
                    </Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverImageChange}
                        className="rounded-lg border"
                      />
                      {coverImage && (
                        <div className="relative inline-block">
                          <img
                            src={
                              typeof coverImage.url === 'string'
                                ? coverImage.url
                                : URL.createObjectURL(coverImage.url)
                            }
                            alt="Cover"
                            className="w-32 h-32 object-cover rounded-lg border"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                            onClick={() => setCoverImage(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 py-6 border-t">
            <Button type="submit" disabled={submitting.value}>
              {mode === 'edit' ? 'แก้ไข' : 'สร้าง'}
            </Button>
            <Button type="button" variant="outline" onClick={handleBack}>
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
